import { z } from 'zod';

/**
 * Client-safe zod schemas + inferred types for the `search_activities` tool.
 *
 * Kept in a dedicated file (NO `server-only` import) so client components
 * can `safeParse` dynamic `tool.input`/`tool.output` blobs from `UIMessage`
 * parts without pulling in the server-only handler module.
 */

export type SearchedActivity = {
    id: string;
    title: string;
    summary: string;
    url: string;
    priceFrom?: number;
    /** `true` when the activity has no known price (FUN-4958) — render "price on request", never €0. */
    priceOnRequest?: boolean;
    location?: string;
    minPartySize?: number;
    maxPartySize?: number;
    tags: ReadonlyArray<string>;
};

export const SearchActivitiesFiltersSchema = z.object({
    query: z.string(),
    locationLabel: z.string().optional(),
    minBudget: z.number().optional(),
    maxBudget: z.number().optional(),
    participants: z.number().optional(),
    // Structured filters the AI applied, echoed so the grid + chat chips reflect them
    // (and replace stale URL values). Same enum values as the sidebar filters, so they
    // map straight onto the URL params. `traits`/`duration`/`spokenLanguage` are NOT
    // echoed here — the URL models them differently (category ids / minutes / id list).
    // `locationType`/`season`/`cleanliness` are applied as SOFT ranking signals server-side
    // (FUN-4912), not hard filters — still echoed here so the chips show intent applied.
    locationType: z.array(z.enum(['INDOOR', 'OUTDOOR', 'VIRTUAL'])).optional(),
    season: z.array(z.enum(['WINTER', 'SPRING', 'SUMMER', 'FALL'])).optional(),
    cleanliness: z.array(z.enum(['DIRTY', 'WET', 'CLEAN'])).optional(),
    // The AI's own trait/duration vocabulary. The sidebar models these differently
    // (traits as 1–3 importance levels, duration as minute bounds), so they don't map to
    // the URL — but echoing them lets the chat chips show what the AI actually applied.
    traits: z.array(z.enum(['teamwork', 'competition', 'action', 'learning'])).optional(),
    duration: z.enum(['short', 'halfDay', 'fullDay']).optional(),
    /** Category NAMES the AI applied as a hard filter (e.g. ["Kookworkshops"]). */
    categories: z.array(z.string()).optional(),
    /**
     * ROOT catalogue category ids the names resolved to, via the SAME resolver
     * pgvector uses. Written to the URL `categories` param so the grid + drawer
     * root toggles + chips reflect the exact ids the vector path matched. Absent
     * when no root name matched.
     */
    categoryIds: z.array(z.number().int().positive()).optional(),
    /**
     * SUBcategory ids the names resolved to. Modelled separately from `categoryIds`
     * because the URL (`subCategories`), drawer (sub toggles), and ES (`sub_categories`
     * field) keep subcategories on their own axis — a sub id in the root slot matches
     * nothing. Absent when no subcategory name matched.
     */
    subCategoryIds: z.array(z.number().int().positive()).optional(),
    // Coordinate distance search, echoed so the grid can write the city-search URL
    // params (distance chip + pills + `sort=distance.ASC`). Set together once the
    // tool geocoded `locationLabel`; absent for a plain region-label turn.
    nearLat: z.number().optional(),
    nearLng: z.number().optional(),
    /** Human-readable place the coords resolve to (= the input `locationLabel`). */
    nearLabel: z.string().optional(),
    /** Radius (km) around `nearLat`/`nearLng`. */
    radius: z.number().optional(),
    /** Route filter: FIXED = "I go to the partner", CUSTOM = "partner comes to me".
     * Maps directly onto the URL `location` param values. */
    route: z.array(z.enum(['FIXED', 'CUSTOM'])).optional(),
    /** Ordering intent the AI applied. Maps onto the URL `sort` param so the
     * filters/ES grid orders accordingly (the vector path already reorders the id set). */
    sort: z.enum(['price.ASC', 'price.DESC', 'popularity.DESC', 'distance.ASC']).optional(),
});
export type SearchActivitiesFilters = z.infer<typeof SearchActivitiesFiltersSchema>;

export const SearchActivitiesTopActivitySchema = z.object({
    id: z.string(),
    title: z.string(),
    url: z.string(),
    /** Short description of the activity — the ONLY source for answering "what's in this activity" questions. */
    summary: z.string().optional(),
    /** Fuller description beyond `summary`, truncated (top-N structured grounding, ADR 0005 D2). */
    description: z.string().optional(),
    /** Top-level catalogue category names — grounds "what type of activity is this" answers. */
    categories: z.array(z.string()).optional(),
    /** Team-dynamic trait labels (teamwork/action/competition/learning) — same vocabulary as the `traits` search filter. */
    traits: z.array(z.string()).optional(),
    /** Active-variant duration range in minutes — the source for "how long does this take?". */
    durationMinutes: z.object({ min: z.number(), max: z.number() }).optional(),
    priceFrom: z.number().optional(),
    /** `true` when the activity has no known price (FUN-4958) — the reply must say "price on request", never €0. */
    priceOnRequest: z.boolean().optional(),
    /** Representative region/city name — the source for "where does this happen?". */
    location: z.string().optional(),
    imageUrl: z.string().optional(),
    /** Participant range — the source for answering "with how many people can I do X?". */
    minPartySize: z.number().optional(),
    maxPartySize: z.number().optional(),
});
export type SearchActivitiesTopActivity = z.infer<typeof SearchActivitiesTopActivitySchema>;

/**
 * Locale-aware result summary. Shared by the server tool (built at search time
 * from the raw pgvector count) AND the client chat UI (rebuilt with the grid's
 * post-filter total so the summary and the "Show all N results" button agree).
 */
export function buildSummary(
    /** `undefined` = a filters-only turn: the grid computes the total itself. */
    count: number | undefined,
    filters: {
        participants?: number;
        locationLabel?: string;
        minBudget?: number;
        maxBudget?: number;
    },
    requestedLimit?: number,
): { en: string; nl: string; fr: string } {
    const constraints: string[] = [];
    if (filters.participants !== undefined) constraints.push(`${filters.participants} pers.`);
    if (filters.locationLabel) constraints.push(filters.locationLabel);
    if (filters.minBudget !== undefined && filters.maxBudget !== undefined) {
        constraints.push(`€${filters.minBudget}–€${filters.maxBudget} p.p.`);
    } else if (filters.maxBudget !== undefined) {
        constraints.push(`≤ €${filters.maxBudget} p.p.`);
    } else if (filters.minBudget !== undefined) {
        constraints.push(`≥ €${filters.minBudget} p.p.`);
    }
    const tail = constraints.length > 0 ? ` (${constraints.join(', ')})` : '';

    if (count === undefined) {
        return {
            en: `Filters applied${tail} — the list shows every match.`,
            nl: `Filters toegepast${tail} — de lijst toont alle resultaten.`,
            fr: `Filtres appliqués${tail} — la liste affiche tous les résultats.`,
        };
    }

    if (count === 0) {
        return {
            en: `No matches yet${tail}. Try widening the budget or location.`,
            nl: `Nog geen matches${tail}. Probeer het budget of de locatie te verbreden.`,
            fr: `Aucun résultat${tail}. Essayez d'élargir le budget ou la zone.`,
        };
    }

    if (requestedLimit !== undefined && count < requestedLimit) {
        return {
            en: `Only ${count} activities match${tail} — fewer than the ${requestedLimit} you asked for.`,
            nl: `Slechts ${count} activiteiten voldoen${tail} — minder dan de ${requestedLimit} die je vroeg.`,
            fr: `Seulement ${count} activités correspondent${tail} — moins que les ${requestedLimit} demandées.`,
        };
    }

    return {
        en: `Found ${count} matching activities${tail}.`,
        nl: `${count} activiteiten gevonden${tail}.`,
        fr: `${count} activités trouvées${tail}.`,
    };
}

/**
 * The resolved semantic request — the embed query + every hard-filter param —
 * echoed on the tool output so the grid can re-run the SAME search with a higher
 * `limit` to fetch the next-most-relevant activities (infinite scroll). Excludes
 * `vatRate`/`tenantId`/`limit`: those are page/request context the grid supplies.
 * Mirrors `searchActivitiesBySemantic`'s `SearchInput` (minus the excluded keys).
 */
export const SemanticMoreRequestSchema = z.object({
    query: z.string(),
    /** Raw query for the title-match boost, so a load-more page ranks like page 1. */
    lexicalQuery: z.string().optional(),
    language: z.enum(['nl', 'fr', 'en']),
    /** Full site locale (e.g. "nl-BE") — needed so infinite-scroll pages keep the
     * same country scope as the first search (else NL-only activities leak in). */
    siteLocale: z.string().optional(),
    participants: z.number().optional(),
    minBudget: z.number().optional(),
    maxBudget: z.number().optional(),
    searchMode: z.enum(['combined', 'language']).optional(),
    /** Keeps a load-more page on the same retrieval path as page 1. */
    skipSemantic: z.boolean().optional(),
    excludeKeywords: z.array(z.string()).optional(),
    /** Curated exclusion families (FUN-4940, e.g. `['alcohol']`), expanded server-side. */
    excludeFamilies: z.array(z.enum(['alcohol', 'danger', 'physical'])).optional(),
    locationType: z.array(z.enum(['INDOOR', 'OUTDOOR', 'VIRTUAL'])).optional(),
    locationLabel: z.string().optional(),
    categories: z.array(z.string()).optional(),
    duration: z.enum(['short', 'halfDay', 'fullDay']).optional(),
    traits: z.array(z.enum(['teamwork', 'competition', 'action', 'learning'])).optional(),
    intensity: z.enum(['relaxed', 'intense']).optional(),
    season: z.array(z.enum(['WINTER', 'SPRING', 'SUMMER', 'FALL'])).optional(),
    spokenLanguage: z.enum(['NL', 'FR', 'EN']).optional(),
    cleanliness: z.array(z.enum(['DIRTY', 'WET', 'CLEAN'])).optional(),
    // Coordinate distance search, carried so infinite-scroll re-runs the SAME
    // distance filter + sort WITHOUT re-geocoding (the tool resolved the coords once).
    near: z.object({ latitude: z.number(), longitude: z.number() }).optional(),
    radius: z.number().nullish(),
    route: z.array(z.enum(['FIXED', 'CUSTOM'])).optional(),
    // FUN-4960: keeps the venue cap on comes-to-us activities across infinite-scroll re-runs.
    radiusIsExplicit: z.boolean().optional(),
});
export type SemanticMoreRequest = z.infer<typeof SemanticMoreRequestSchema>;

export const SearchActivitiesOutputSchema = z.object({
    /**
     * Result count. Both retrieval paths return one, so it is always present on a
     * fresh search — optional only
     * so older stored messages (from before this migration, when a "filters"
     * turn carried no count at all) still parse.
     */
    count: z.number().optional(),
    /**
     * Echoed semantic request for the "keep loading more" infinite scroll — see
     * `SemanticMoreRequestSchema`. Optional so older messages still parse.
     */
    semanticRequest: SemanticMoreRequestSchema.optional(),
    /**
     * VAT mode the tool actually used for budget interpretation + returned
     * prices. Echoed only when the user EXPLICITLY asked for incl/excl
     * ("inclusief btw", "TVA incluse", "ex VAT", …). The client flips the
     * page-wide VAT preference to match so the grid renders in the same mode.
     */
    appliedVatMode: z.enum(['inclusive', 'exclusive']).optional(),
    /**
     * The amount the user asked for, if any (the tool's `limit` input). Present
     * only when the user requested a specific number. Compare against `count`:
     * when `count < requestedLimit`, fewer activities exist than requested and
     * the reply MUST say so explicitly.
     */
    requestedLimit: z.number().int().positive().optional(),
    summaryByLocale: z.object({ en: z.string(), nl: z.string(), fr: z.string() }),
    topActivities: z.array(SearchActivitiesTopActivitySchema),
    /** Ranked activity IDs from pgvector — written to the URL so the grid hard-filters in AI mode. */
    activityIds: z.array(z.number().int().positive()),
    /**
     * Ranked activities paired with their activity group (ADR 0007), aligned 1:1
     * with `activityIds`. The client caches these so hydration resolves each
     * group's PRIMARY representative live by group id against the catalogue —
     * region-eligible, filter-respecting — rather than hydrating the exact member
     * the AI matched. `groupId` is `null` only for ungrouped rows. Optional so
     * older stored messages (pre-ADR-0007, `activityIds` only) still parse; the
     * client then falls back to treating every id as ungrouped.
     */
    activityGroups: z
        .array(
            z.object({
                activityId: z.number().int().positive(),
                groupId: z.number().int().positive().nullable(),
            }),
        )
        .optional(),
    /**
     * Which blend the code-derived RRF weighting shifted toward (FUN-4918):
     * `'structural'` (request fully captured by structured filters — leans on
     * lexical matching + popularity) or `'subjective'` (theme/audience/vibe/
     * exclusion/intensity — leans on the semantic arm). Retrieval is ALWAYS the
     * same fused hybrid pipeline; only the blend weights shift. Admin-only UI
     * surfaces this as a badge. Optional so older messages still parse.
     */
    retrievalStrategy: z.enum(['structural', 'subjective']).optional(),
    /**
     * Model-facing instruction embedded in the result itself. Set on filters-only
     * turns, where `activityIds` is deliberately empty (the grid runs the search) —
     * without it the model reads the empty array as zero results and apologises.
     */
    note: z.string().optional(),
    /**
     * Human-readable explanation for why the tool derived that blend.
     * Admin-only UI surfaces this next to the badge. Optional so older
     * messages still parse.
     */
    retrievalReason: z.string().optional(),
    /**
     * Model id actually used for the conversation-aware listwise rerank
     * (FUN-4917), echoed only when that rerank ran. Feeds the client
     * `ai_search.results_shown` analytics event's `model_ranking` field.
     */
    modelRanking: z.string().optional(),
    /**
     * Per-activity relevancy/popularity scores, aligned by `id`. Admin-only UI
     * surfaces these on the cards; carried through `ActivityAiResultsBridge` into
     * a per-chat store. Optional so older messages without scores still parse.
     */
    scores: z
        .array(
            z.object({
                id: z.number().int().positive(),
                similarity: z.number(),
                rankScore: z.number(),
                popularityBoost: z.number(),
            }),
        )
        .optional(),
    filters: SearchActivitiesFiltersSchema,
    /**
     * Entropy-based recommend-vs-clarify routing (ADR 0005 D3). Computed purely
     * from the rerank score distribution — no catalogue content — so the model
     * can branch the reply without extra items being injected into context.
     * `clarify` means the top scores are too close together to confidently pick
     * a winner; the model should ask exactly ONE clarifying question instead of
     * recommending. Optional so older persisted messages still parse.
     */
    guidedDiscovery: z
        .object({
            decision: z.enum(['recommend', 'clarify']),
            entropy: z.number(),
        })
        .optional(),
});
export type SearchActivitiesOutput = z.infer<typeof SearchActivitiesOutputSchema>;
